// Turns the customer's own lists into what the portal home shows: headline
// numbers, things that need their attention, and what is coming up.
import { expiryInfo, isOverdueInvoice, summariseBilling } from './customerView';

const DAY_MS = 86400000;

export const greetingFor = (hour) => {
  if (hour < 5) return 'Hello';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const OPEN_TICKET = ['open', 'in_progress', 'pending'];

// Renewals in the next `withinDays` days (and anything already past), soonest first:
// subscriptions by next invoice date, domains by expiry date.
const upcomingEvents = (subscriptions, domains, now, withinDays) => {
  const events = [];
  (subscriptions || []).forEach((s) => {
    if (!s.next_due_date || (s.status && String(s.status).toLowerCase() !== 'active')) return;
    events.push({ kind: 'subscription', label: s.description || 'Subscription', date: s.next_due_date, amount: s.recurring_amount, currency: s.currency });
  });
  (domains || []).forEach((d) => {
    if (!d.expiry_date) return;
    events.push({ kind: 'domain', label: d.domain_name, date: d.expiry_date });
  });
  return events
    .map((e) => ({ ...e, info: expiryInfo(e.date, now) }))
    .filter((e) => e.info && e.info.days <= withinDays)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
};

export const summarisePortal = ({ invoices = [], subscriptions = [], quotes = [], tickets = [], domains = [], sites = [] }, now = new Date()) => {
  const billing = summariseBilling(invoices, now);
  const unpaid = invoices.filter((i) => i.payment_status === 'Pending Payment');
  const outstanding = unpaid.reduce((sum, i) => sum + Number(i.amount_payable || 0), 0);
  const overdue = invoices.filter((i) => isOverdueInvoice(i, now));
  const openTickets = tickets.filter((t) => OPEN_TICKET.includes(t.status));
  const awaitingReply = tickets.filter((t) => t.status === 'pending');
  const quotesToReview = quotes.filter((q) => q.status === 'sent');
  const expiringDomains = domains.filter((d) => {
    const info = expiryInfo(d.expiry_date, now);
    return info && info.days <= 30;
  });
  const downSites = sites.filter((s) => s.state === 'down');
  const activeSubscriptions = subscriptions.filter((s) => !s.status || String(s.status).toLowerCase() === 'active');

  // What needs doing, most urgent first. Each item links to where it is fixed.
  const attention = [];
  if (downSites.length) attention.push({ key: 'down', tone: 'bad', path: '/portal/uptime', text: downSites.length === 1 ? `${downSites[0].hostname} looks down right now` : `${downSites.length} of your sites look down right now` });
  if (overdue.length) attention.push({ key: 'overdue', tone: 'bad', path: '/portal/invoices', text: `${overdue.length} overdue invoice${overdue.length === 1 ? '' : 's'}` });
  if (awaitingReply.length) attention.push({ key: 'reply', tone: 'warn', path: '/portal/tickets', text: `${awaitingReply.length} support request${awaitingReply.length === 1 ? '' : 's'} waiting for your reply` });
  if (quotesToReview.length) attention.push({ key: 'quotes', tone: 'warn', path: '/portal/quotes', text: `${quotesToReview.length} quote${quotesToReview.length === 1 ? '' : 's'} to review` });
  if (expiringDomains.length) attention.push({ key: 'domains', tone: 'warn', path: '/portal/domains', text: `${expiringDomains.length} domain${expiringDomains.length === 1 ? '' : 's'} expiring within 30 days` });

  const byDateDesc = (field) => (a, b) => new Date(b[field] || 0) - new Date(a[field] || 0);

  return {
    billing,
    outstanding,
    overdueCount: overdue.length,
    openTicketCount: openTickets.length,
    activeSubscriptionCount: activeSubscriptions.length,
    monthlyRecurring: activeSubscriptions.filter((s) => s.billing_cycle === 'monthly').reduce((sum, s) => sum + Number(s.recurring_amount || 0), 0),
    domainCount: domains.length,
    attention,
    upcoming: upcomingEvents(subscriptions, domains, now, 60).slice(0, 5),
    recentTickets: [...tickets].sort(byDateDesc('created_at')).slice(0, 3),
    recentInvoices: [...invoices].sort(byDateDesc('creation_date')).slice(0, 3),
    nextRenewal: upcomingEvents(subscriptions, domains, now, 36500)[0] || null,
    nextDomain: upcomingEvents([], domains, now, 36500)[0] || null
  };
};

export { DAY_MS };
